import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/hooks/useAuth';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { useAppearanceMode } from '@/hooks/useAppearanceMode';
import { EditProfileDialog } from '@/components/profile';
import { MyNotificationsCard } from '@/components/settings';
import { LogOut, Moon } from 'lucide-react';

export default function SettingsPage() {
  const { t } = useTranslation();
  const { signOut, profile } = useAuth();
  const { confirmShiftCancel, setConfirmShiftCancel, loading } = useUserPreferences();
  const { isDark, setMode } = useAppearanceMode();
  const [editProfileOpen, setEditProfileOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="space-y-6">
      {/* Main heading section */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          {t('settings.title', 'Settings')}
        </h1>
        <p className="text-muted-foreground">
          {t('settings.description', 'Manage your account settings and preferences')}
        </p>
      </div>

      {/* Profile section */}
      <Card>
        <CardHeader>
          <CardTitle as="h2">
            {t('settings.profile', 'Profile')}
          </CardTitle>
          <CardDescription>
            {t('settings.profileDescription', 'Manage your personal information and preferences')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border rounded-lg">
            <div>
              <h3 className="font-medium">{t('settings.editProfile', 'Edit Profile')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('settings.editProfileDescription', 'Update your personal information, contact details, and preferences')}
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setEditProfileOpen(true)}
              className="w-full sm:w-auto flex items-center gap-2"
            >
              {t('settings.editProfile', 'Edit Profile')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Scheduling section */}
      <Card>
        <CardHeader>
          <CardTitle as="h2">
            {t('settings.scheduling', 'Scheduling')}
          </CardTitle>
          <CardDescription>
            {t('settings.schedulingDescription', 'Confirmation and safety options')}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4">
          <div>
            <div className="font-medium">
              {t('settings.confirmShiftCancel', 'Confirm before cancelling a shift')}
            </div>
            <div className="text-sm text-muted-foreground">
              {t('settings.confirmShiftCancelDescription', 'Prevent accidental deletions by showing a confirmation dialog.')}
            </div>
          </div>
          <Switch
            checked={!!confirmShiftCancel}
            disabled={loading}
            onCheckedChange={(checked) => setConfirmShiftCancel(Boolean(checked))}
          />
        </CardContent>
      </Card>
      {/* Notifications section */}
      <MyNotificationsCard />

      {/* Appearance section */}
      <Card>
        <CardHeader>
          <CardTitle as="h2">
            <span className="flex items-center gap-2">
              <Moon className="h-5 w-5" />
              {t('settings.appearance', 'Appearance')}
            </span>
          </CardTitle>
          <CardDescription>
            {t('settings.appearanceDescription', 'Customize how the app looks on your device')}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4">
          <div>
            <div className="font-medium">
              {t('settings.darkMode', 'Dark Mode')}
            </div>
            <div className="text-sm text-muted-foreground">
              {t('settings.darkModeDescription', 'Switch between light and dark themes for the interface.')}
            </div>
          </div>
          <Switch
            checked={isDark}
            onCheckedChange={(checked) => setMode(checked ? 'dark' : 'light')}
          />
        </CardContent>
      </Card>

      {/* Account Actions section */}
      <Card>
        <CardHeader>
          <CardTitle as="h2">
            {t('settings.accountActions', 'Account Actions')}
          </CardTitle>
          <CardDescription>
            {t('settings.accountActionsDescription', 'Manage your account settings and preferences')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border rounded-lg">
            <div>
              <h3 className="font-medium">{t('settings.signOut', 'Sign Out')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('settings.signOutDescription', 'Sign out of your account and return to the login page')}
              </p>
            </div>
            <Button variant="outline" onClick={handleSignOut} className="w-full sm:w-auto flex items-center gap-2">
              <LogOut className="h-4 w-4" />
              {t('auth.signOut', 'Sign Out')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Edit Profile Dialog */}
      <EditProfileDialog 
        open={editProfileOpen} 
        onOpenChange={setEditProfileOpen} 
      />
    </div>
  );
}
